import i18n from '@/lib/i18n'

const TIMEZONE_STORAGE_KEY = 'edge_manager_timezone'

/**
 * Returns the resolved IANA timezone string.
 * If user has set a preference, returns that; otherwise returns browser default.
 */
export function getTimezone(): string {
  const pref = localStorage.getItem(TIMEZONE_STORAGE_KEY)
  if (pref && pref !== 'browser') {
    return pref
  }
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * Saves timezone preference to localStorage.
 * Pass 'browser' to use browser default.
 */
export function setTimezone(tz: string): void {
  localStorage.setItem(TIMEZONE_STORAGE_KEY, tz)
}

/**
 * Returns the raw timezone preference: 'browser' or an IANA timezone string.
 */
export function getTimezonePreference(): string {
  return localStorage.getItem(TIMEZONE_STORAGE_KEY) || 'browser'
}

/**
 * Returns a list of common IANA timezones for the selector dropdown.
 */
export function getCommonTimezones(): { value: string; label: string }[] {
  return [
    { value: 'America/New_York', label: 'Eastern Time (US)' },
    { value: 'America/Chicago', label: 'Central Time (US)' },
    { value: 'America/Denver', label: 'Mountain Time (US)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (US)' },
    { value: 'America/Anchorage', label: 'Alaska Time' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time' },
    { value: 'America/Toronto', label: 'Eastern Time (Canada)' },
    { value: 'America/Vancouver', label: 'Pacific Time (Canada)' },
    { value: 'America/Sao_Paulo', label: 'Brasilia Time' },
    { value: 'America/Argentina/Buenos_Aires', label: 'Argentina Time' },
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Central European Time' },
    { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
    { value: 'Europe/Moscow', label: 'Moscow Time' },
    { value: 'Asia/Dubai', label: 'Gulf Standard Time' },
    { value: 'Asia/Kolkata', label: 'India Standard Time' },
    { value: 'Asia/Singapore', label: 'Singapore Time' },
    { value: 'Asia/Shanghai', label: 'China Standard Time' },
    { value: 'Asia/Tokyo', label: 'Japan Standard Time' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
    { value: 'Australia/Perth', label: 'Perth (AWST)' },
    { value: 'Pacific/Auckland', label: 'New Zealand Time' },
    { value: 'UTC', label: 'UTC' },
  ]
}

/**
 * Parses a timestamp into a Date object.
 * Handles ISO 8601 strings and epoch values (seconds or milliseconds).
 */
export function parseTimestamp(timestamp: unknown): Date | null {
  if (timestamp === null || timestamp === undefined || timestamp === '') return null

  let date: Date

  if (typeof timestamp === 'string') {
    // Numeric strings are epoch values; everything else is an ISO/date string.
    const asNumber = Number(timestamp)
    if (timestamp.trim() !== '' && !Number.isNaN(asNumber)) {
      // Treat 10-digit values as seconds, 13-digit as milliseconds.
      date = new Date(asNumber < 1e12 ? asNumber * 1000 : asNumber)
    } else {
      date = new Date(timestamp)
    }
  } else if (typeof timestamp === 'number') {
    date = new Date(timestamp < 1e12 ? timestamp * 1000 : timestamp)
  } else {
    return null
  }

  if (isNaN(date.getTime())) return null
  return date
}

/**
 * Formats a timestamp as a full date+time string.
 * Example: "Jan 15, 2025, 10:30:00 AM"
 */
export function formatDateTime(timestamp: unknown, fallback = 'Never'): string {
  const date = parseTimestamp(timestamp)
  if (!date) return fallback

  return date.toLocaleString(i18n.language, {
    timeZone: getTimezone(),
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

/**
 * Formats a timestamp in compact form (no year).
 * Example: "Jan 15, 10:30:00 AM"
 */
export function formatDateTimeCompact(
  timestamp: unknown,
  fallback = ''
): string {
  const date = parseTimestamp(timestamp)
  if (!date) return fallback

  return date.toLocaleString(i18n.language, {
    timeZone: getTimezone(),
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

/**
 * Formats a timestamp as relative time ("2h ago", "3d ago")
 * or falls back to a localized date for older timestamps.
 */
export function formatRelativeTime(
  timestamp: unknown,
  fallback = 'Never'
): string {
  const date = parseTimestamp(timestamp)
  if (!date) return fallback

  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const hours = Math.floor(diff / (60 * 60 * 1000))

  if (hours < 1) {
    const minutes = Math.floor(diff / (60 * 1000))
    if (minutes < 1) return i18n.t('time.justNow')
    return i18n.t('time.minutesAgo', { count: minutes })
  }
  if (hours < 24) return i18n.t('time.hoursAgo', { count: hours })

  const days = Math.floor(hours / 24)
  if (days < 7) return i18n.t('time.daysAgo', { count: days })

  return date.toLocaleDateString(i18n.language, {
    timeZone: getTimezone(),
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}
